import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import { PlayerEvent, SeekableRange } from '@smart-tv/components/player/types';
import usePlayerEvent from '@smart-tv/components/player/_hooks/use-player-event';
import usePlayer from '@smart-tv/components/player/use-player';
import React from 'react';
import { useAtom } from 'jotai';
import { descalePlayerAtom } from '../../atoms';

const useVodPlayerScreen = () => {
  const { player, setIsPlaying } = usePlayerContext();
  const { handlePlayerRoutingOnClose } = usePlayer({});
  const [descalePlayer, setDescalePlayer] = useAtom(descalePlayerAtom);
  const completedTriggeredRef = React.useRef(false);
  const stallTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const handleBack = React.useCallback(() => {
    descalePlayer ? setDescalePlayer(false) : handlePlayerRoutingOnClose();
  }, [descalePlayer, handlePlayerRoutingOnClose, setDescalePlayer]);

  const handlePlay = React.useCallback(() => {
    player?.play();
    setIsPlaying(true);
  }, [player, setIsPlaying]);

  const handlePause = React.useCallback(() => {
    player?.pause();
    setIsPlaying(false);
  }, [player, setIsPlaying]);

  const handlePlayCompleted = React.useCallback(() => {
    handlePlayerRoutingOnClose();
  }, [handlePlayerRoutingOnClose]);

  const handlePlayTimeUpdated = React.useCallback(
    (seekableRange: SeekableRange) => {
      if (completedTriggeredRef.current) return;

      const position = seekableRange?.position;
      const duration = seekableRange?.duration;

      if (position == null || duration == null || duration <= 0) return;

      const remaining = duration - position;

      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = undefined;
      }

      if (remaining < 1) {
        stallTimerRef.current = setTimeout(() => {
          if (!completedTriggeredRef.current) {
            completedTriggeredRef.current = true;
            handlePlayCompleted();
          }
        }, 2000);
      }
    },
    [handlePlayCompleted]
  );

  usePlayerEvent(PlayerEvent.PlayCompleted, handlePlayCompleted);
  usePlayerEvent(PlayerEvent.PlayTimeUpdated, handlePlayTimeUpdated);

  React.useEffect(() => {
    return () => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
      }
    };
  }, []);

  return {
    handleBack,
    handlePlay,
    handlePause,
    handlePlayCompleted
  };
};

export default useVodPlayerScreen;
